import clsx from 'clsx'
import React from 'react'
import toast from 'react-hot-toast'
import { useMutation } from 'react-query'
import { Outlet, useParams, useLocation } from 'react-router-dom'
import BreadCrumbs from '../components/BreadCrumbs'
import DeleteButton from '../components/DeleteButton'
import ErrorPage from '../components/ErrorPage'
import PageNav from '../components/PageNav'
import Spinner from '../components/Spinner'
import { useAppUser } from '../hooks/useAppUsers'
import endUsersService from '../services/endUsersService'
import { queryClient } from '../utils/react-query'
import { BuzzlUsersError } from '../types/buzzl-user'

const AppUser: React.FC = () => {
  const { pathname } = useLocation()
  const { id } = useParams<{ id: string }>()
  const { data, isLoading, isError } = useAppUser()

  const handleSuspension = useMutation(endUsersService.suspendEndUser, {
    onSuccess: async () => {
      queryClient.refetchQueries('endUser')
      toast.success('User updated.')
    },
    onError: (e) => {
      const error = e as BuzzlUsersError
      error.response.errors.map((er) =>
        toast.error(er.message || 'Something went wrong, if the error persist please contact support.')
      )
    },
  })

  const breadCrumbPages = [
    { name: 'Mobile App Users', href: '/app-users' },
    { name: 'View User', href: pathname },
  ]

  const navigation = [
    { name: 'Details', href: `/app-users/${id}` },
    { name: 'Experiences', href: `experiences` },
  ]

  if (isError) {
    return (
      <ErrorPage
        title="Something went wrong!"
        text="We keep track of these errors, but feel free to contact us if refreshing doesn't fix things."
      />
    )
  }

  if (!isLoading && !data) {
    return <ErrorPage title="Something went wrong!" text="This user does not exist." />
  }

  return (
    <div>
      <BreadCrumbs breadCrumbPages={breadCrumbPages} />
      {data && <h1 className="page-title">{data.user.displayName}</h1>}
      <div className="flex items-start justify-between">
        <div className="flex-1 md:mr-5">
          <PageNav navigation={navigation} />
        </div>
        <DeleteButton
          type="button"
          className={clsx({
            btn: true,
            'btn-delete': !data?.isSuspended,
            'btn-primary': data?.isSuspended,
          })}
          modalTitle={data?.isSuspended ? 'Activate user?' : 'Suspend User'}
          alert={data?.isSuspended}
          modalText={
            data?.isSuspended
              ? 'You are about to activate this user. This action can be undone at any stage. Are you sure you want to activate this user?'
              : 'You are about to suspend this user. This action can be undone at any stage. Are you sure you want to suspend this user?'
          }
          disabled={handleSuspension.isLoading}
          onClick={async () => {
            if (id) {
              await handleSuspension.mutateAsync({
                id,
                isSuspended: !data?.isSuspended,
              })
            }
          }}
        >
          {handleSuspension.isLoading ? (
            <div className="mr-3">
              <Spinner className="trailing-icon h-4 w-4" />
            </div>
          ) : null}
          {data?.isSuspended ? 'Activate user' : 'Suspend User'}
        </DeleteButton>
      </div>
      {isLoading ? (
        <div className="flex h-32 items-center justify-center">
          <Spinner className="h-10 w-10" />
        </div>
      ) : (
        <Outlet context={data} />
      )}
    </div>
  )
}

export default AppUser
